import ArticleMeta from "@dogstrust/dt-web-core-lib-front-end/src/components/ArticleMeta";
import { LinkNoStyle } from "@dogstrust/dt-web-core-lib-front-end/src/components/common/LinkNoStyle/LinkNoStyle";
import SocialSharing from "@dogstrust/dt-web-core-lib-front-end/src/components/common/SocialSharing/SocialSharing";
import SEO from "@dogstrust/dt-web-core-lib-front-end/src/components/seo";
import IntroToDisplay from "@dogstrust/dt-web-core-lib-front-end/src/containers/introBlocks/IntroToDisplay";
import type { IntroType } from "@dogstrust/dt-web-core-lib-front-end/src/containers/introBlocks/IntroToDisplay";
import { ParagraphSections } from "@dogstrust/dt-web-core-lib-front-end/src/containers/paragraphs/ParagraphSections/ParagraphSections";
import ParagraphTextArea from "@dogstrust/dt-web-core-lib-front-end/src/containers/paragraphs/ParagraphTextArea/ParagraphTextArea";
import SectionArticleOrEventCards from "@dogstrust/dt-web-core-lib-front-end/src/containers/sections/SectionArticleOrEventCards";
import SectionHelpAdviceNewsletterPromo from "@dogstrust/dt-web-core-lib-front-end/src/containers/sections/SectionHelpAdviceNewsletterPromo";
import { usePageManager } from "@dogstrust/dt-web-core-lib-front-end/src/context";
import { Layout } from "@dogstrust/uk/components/Layout/Layout";

import { formatDate } from "@dogstrust/dt-web-core-lib-front-end/src/utils/dates";
import { getImagePath } from "@dogstrust/dt-web-core-lib-front-end/src/utils/images";
import { mapContentUnionToSignPostCardProps } from "@dogstrust/dt-web-core-lib-front-end/src/utils/mappers";
import { getSeoProps } from "@dogstrust/dt-web-core-lib-front-end/src/utils/seo";

import { Head as DefaultHead } from "@dogstrust/dt-web-core-lib-front-end/src/components/Head/Head";
import { graphql } from "gatsby";
import React, { useEffect } from "react";
import * as styles from "./OurStoryPage.module.scss";
import { Pencil } from "@dogstrust/dt-web-core-lib-front-end/src/components/common/Icons";

interface Props {
	data: Queries.OurStoryPageQuery;
}

export const Head: React.FC<Props> = ({
	data: {
		nodeOurStory: {
			title,
			path,
			field_hreflang_url,
			field_short_description,
			field_meta_tags,
			relationships: { field_featured_image },
		},
	},
}) => {
	return (
		<DefaultHead pageUrl={path.alias} hrefLangUrl={field_hreflang_url}>
			<SEO
				{...getSeoProps(field_meta_tags, {
					title: `${title} | Dogs Trust`,
					description: field_short_description,
					canonical_url: process.env.GATSBY_DOMAIN_NAME + path.alias,
					og_type: "article",
					og_url: process.env.GATSBY_DOMAIN_NAME + path.alias,
					og_title: `${title} | Dogs Trust`,
					og_description: field_short_description,
					og_image_url: `${getImagePath(
						"400x300",
						field_featured_image?.field_media_image?.uri,
					)}`,
					twitter_cards_type: "summary_large_image",
					twitter_cards_description: field_short_description,
					twitter_cards_site: "@DogsTrust",
					twitter_cards_title: `${title} | Dogs Trust`,
					twitter_cards_page_url: process.env.GATSBY_DOMAIN_NAME + path.alias,
					twitter_cards_image: `${getImagePath(
						"400x300",
						field_featured_image?.field_media_image?.uri,
					)}`,
				})}
			/>
		</DefaultHead>
	);
};

const OurStoryPage: React.FC<Props> = ({
	data: {
		nodeOurStory: {
			title,
			path,
			breadcrumbs,
			field_author,
			field_date,
			field_short_description,
			field_body,
			field_show_newsletter_promo,
			relatedStoryTopicTags,
			relationships: {
				field_page_sections,
				field_featured_image,
				field_related_help_advice_articl,
				field_author_group
			},
		},
		newsletterPromo,
	},
}) => {
	const { usePagination, setWithNoBreaks } = usePageManager();

	// biome-ignore lint/correctness/useExhaustiveDependencies: We only want this to run once
	useEffect(() => {
		usePagination(false);
		setWithNoBreaks(true);

		return () => {
			setWithNoBreaks(false);
		};
	}, []);

	const getLinkFilteredByTopic = (url: string, label: string) => {
		const tokens = url.split("/");
		tokens.pop();
		return `${tokens.join("/")}?selectedTopic=${label}`;
	};
	return (
		<Layout breadcrumbs={breadcrumbs ? [...breadcrumbs] : []}>
			<div className={styles.contentarea}>
				<IntroToDisplay
					key={0}
					introType={"Intro-Hero-With-Image" as IntroType}
					title={title}
					body={
						!!field_short_description && `<p>${field_short_description}</p>`
					}
					imgSrc={field_featured_image?.field_media_image?.uri}
					imgAlt={field_featured_image?.field_media_image?.alt}
				/>
				<div className={styles.date}>
					<div className={styles.datetext}>
						{formatDate(new Date(field_date))}
					</div>
				</div>
				{field_author_group?.title && (
					<div className={styles.authorGroup}>
						<div className={styles.authorGroupContent}>
							<div className={styles.detailssectionitem}>
								<p className={styles.contenttext}>
									<Pencil height={"22px"} width={"22px"} /> Reviewed by:
								</p>
							</div>
							<LinkNoStyle
								to={field_author_group?.path?.alias}
								className={styles.authorGroupLink}
							>
								{field_author_group?.title}
							</LinkNoStyle>
						</div>
					</div>
				)}

				{field_body?.value?.length > 0 ? (
					<ParagraphTextArea htmlText={field_body.value} />
				) : null}
				{field_page_sections.map((section, i) => (
					<ParagraphSections
						key={"id" in section ? section.id : i}
						data={section}
						thisSectionKey={i}
						noBreaks={true}
					/>
				))}
				<div className={styles.sharewrapper}>
					<SocialSharing
						title="If you liked this page, tell the world!"
						shareBodyText="I saw this on the Dogs Trust’s website and thought of you"
						sharePath={process.env.GATSBY_DOMAIN_NAME + path.alias}
					/>
				</div>
				<div className={styles.metawrapper}>
					<ArticleMeta
						author={field_author}
						tags={relatedStoryTopicTags.map((topic) => ({
							title: topic.title,
							url: getLinkFilteredByTopic(path.alias, topic.title),
						}))}
					/>
				</div>
				{field_related_help_advice_articl.length > 0 ? (
					<>
						<div className={styles.relatedwrapper}>
							<SectionArticleOrEventCards
								items={(
									field_related_help_advice_articl as Queries.ContentUnionDataFragment[]
								).map(mapContentUnionToSignPostCardProps)}
								title="Related articles"
								hideFromJumpLinks={true}
							/>
						</div>
					</>
				) : null}
				{field_show_newsletter_promo && (
					<SectionHelpAdviceNewsletterPromo
						title={newsletterPromo.field_front_end_title}
						body={newsletterPromo.field_section_short_description.value}
						buttonText={newsletterPromo.field_link.title}
						buttonUrl={newsletterPromo.field_link.uri}
					/>
				)}
			</div>
		</Layout>
	);
};
export default OurStoryPage;

export const query = graphql`
	
    query OurStoryPage($path: String) {
        nodeOurStory(path: { alias: { eq: $path } }) {
            ...OurStory
        }
        newsletterPromo: nodeSection(
            title: { eq: "Section-Newsletter-Promo" }
        ) {
            field_front_end_title
            field_link {
                title
                uri
            }
            field_section_short_description {
                value
            }
        }
    }
`;
export const fragment = graphql`
    fragment OurStory on node__our_story {
        title
        path {
            alias
        }
        breadcrumbs {
            ...BreadCrumb
        }
		field_hreflang_url
        field_body {
            value
        }
        field_author
        field_short_description
        field_short_link_text
        field_node_weight
        field_date
        field_show_newsletter_promo
        field_meta_tags {
            ...MetaTags
        }
        relatedStoryTopicTags {
            title
        }
        relationships {
            field_page_sections {
                ...ParagraphSectionsData
            }
            field_featured_image {
                field_media_image {
                    uri
                    alt
                }
            }
            field_related_help_advice_articl {
                ...ContentUnionData
            }
            field_category_type {
                name
            }
            field_related_our_story_topics {
                name
            }
            field_author_group {
				title
				path {
					alias
				}
			}
        }
    }
`;
